// src/services/richText.ts
// -----------------------------------------------------------------------------
// A tiny rich-text layer for the Action Sheet templates: Bold / Underline /
// Font-size, edited in a modal WYSIWYG and rendered on screen AND in the jsPDF
// action sheet. Templates are stored as a RESTRICTED HTML subset:
//   <b> (and <strong>), <u>, <span style="font-size:_px">, <br>, plain text.
// Backward compatible: an old plain-text template is valid HTML (no tags) and
// renders unchanged. Design: docs/action-sheet-rich-text-plan.md.
//
// Two exports drive the render targets:
//   sanitizeRichText(html) -> canonical safe HTML (screen render + defensive save)
//   htmlToRuns(html)       -> lines of styled runs (the PDF renderer walks these)

// The three admin-selectable sizes. `px` = on-screen, `pt` = jsPDF. "normal" is
// the base (no span emitted). Keep screen + PDF in step by mapping here.
export const FONT_SIZES = {
  small: { px: 12, pt: 8.5, label: "Small" },
  normal: { px: 14, pt: 9.5, label: "Normal" },
  large: { px: 18, pt: 12, label: "Large" },
} as const;

export type FontSizeKey = keyof typeof FONT_SIZES;
export const BASE_PT = FONT_SIZES.normal.pt;

// Allowed inline font-size values (px string) -> jsPDF pt. 14px (normal) is
// intentionally absent — normal text needs no span.
const SIZE_PX_TO_PT: Record<string, number> = {
  "12px": FONT_SIZES.small.pt,
  "18px": FONT_SIZES.large.pt,
};

export interface RichRun {
  text: string;
  bold: boolean;
  underline: boolean;
  sizePt: number;
}
export type RichLine = RichRun[];

// Convert literal "\n" newlines in text nodes to <br> elements, so every line
// break in the prepared body is a canonical <br>. Without this, mixed content
// double-spaces: the render surfaces use white-space:pre-wrap, so a literal \n
// AND an adjacent <br> each produce a break (seen live 4 Jul 2026).
function newlinesToBrs(body: HTMLElement): void {
  const doc = body.ownerDocument;
  const texts: Text[] = [];
  (function collect(node: Node) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) texts.push(child as Text);
      else collect(child);
    });
  })(body);
  texts.forEach((t) => {
    const val = t.textContent || "";
    if (!val.includes("\n")) return;
    const parent = t.parentNode;
    if (!parent) return;
    const frag = doc.createDocumentFragment();
    val.split("\n").forEach((seg, i) => {
      if (i > 0) frag.appendChild(doc.createElement("br"));
      if (seg) frag.appendChild(doc.createTextNode(seg));
    });
    parent.replaceChild(frag, t);
  });
}

// Parse to a <body> and flatten block elements (div/p) into <br>-delimited inline
// content, so both consumers only ever walk inline tags + <br>.
function prepareBody(html: string): HTMLElement {
  const doc = new DOMParser().parseFromString(`<body>${String(html ?? "")}</body>`, "text/html");
  const body = doc.body;
  newlinesToBrs(body);
  let block = body.querySelector("div, p");
  while (block) {
    const parent = block.parentNode;
    if (!parent) break;
    const isBr = (n: Node | null) => !!n && n.nodeType === 1 && (n as Element).tagName === "BR";
    // Chrome's contentEditable represents an EMPTY line as <div><br></div> —
    // that inner <br> is a height placeholder, not content. Hoisting it AND
    // adding the boundary breaks below doubled every blank line the admin
    // typed (4 Jul 2026). Drop the placeholder; the boundary breaks alone
    // yield exactly one empty line.
    const onlyChild = block.childNodes.length === 1 ? block.firstChild : null;
    if (onlyChild && isBr(onlyChild)) {
      block.removeChild(onlyChild);
    }
    // A break BEFORE the block if it has any previous content that isn't a break.
    if (block.previousSibling && !isBr(block.previousSibling)) {
      parent.insertBefore(doc.createElement("br"), block);
    }
    // Move the block's children up in front of it.
    while (block.firstChild) parent.insertBefore(block.firstChild, block);
    // A break AFTER, if a following sibling would otherwise run on.
    if (block.nextSibling && !isBr(block.nextSibling)) {
      parent.insertBefore(doc.createElement("br"), block);
    }
    parent.removeChild(block);
    block = body.querySelector("div, p");
  }
  return body;
}

function fontSizePx(el: Element): string | null {
  const fs = (el as HTMLElement).style?.fontSize || "";
  const m = /^(\d+(?:\.\d+)?)px$/.exec(fs.trim());
  return m ? `${Math.round(Number.parseFloat(m[1]))}px` : null;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// -----------------------------------------------------------------------------
// sanitizeRichText — whitelist to the canonical inline subset (b / u /
// span[font-size] / br / text). Unknown tags unwrap to their text; script/style
// dropped. Used for the on-screen render and defensively on save.
// -----------------------------------------------------------------------------
export function sanitizeRichText(html: string): string {
  const body = prepareBody(html);

  function clean(node: Node): string {
    let s = "";
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        s += esc(child.textContent || "");
        return;
      }
      if (child.nodeType !== 1) return;
      const el = child as Element;
      switch (el.tagName.toLowerCase()) {
        case "br":
          s += "<br>";
          break;
        case "b":
        case "strong":
          s += `<b>${clean(el)}</b>`;
          break;
        case "u":
          s += `<u>${clean(el)}</u>`;
          break;
        case "span": {
          const px = fontSizePx(el);
          s +=
            px && SIZE_PX_TO_PT[px]
              ? `<span style="font-size:${px}">${clean(el)}</span>`
              : clean(el);
          break;
        }
        case "script":
        case "style":
          break;
        default:
          s += clean(el); // unwrap unknown inline tags (i, em, a, font…)
      }
    });
    return s;
  }

  return clean(body).replace(/(<br>)+$/, "");
}

// -----------------------------------------------------------------------------
// Paragraph gap — a blank line between paragraphs renders at PARA_GAP_RATIO of
// the height a full empty line would take (John, 4 Jul 2026: a whole blank line
// looked too tall on the action sheets). ONE knob for both render targets:
// richTextDisplayHtml (screen) and actionSheetPdf.drawRichText (PDF) derive
// from it, same pattern as FONT_SIZES keeping px/pt in step.
// -----------------------------------------------------------------------------
export const PARA_GAP_RATIO = 0.6;

// Screen: the render surfaces use line-height ~1.6, so a full blank line
// occupies ~1.6em; the gap block replaces it with PARA_GAP_RATIO of that.
const PARA_GAP_EM = Math.round(PARA_GAP_RATIO * 1.6 * 100) / 100;
const PARA_GAP_HTML = `<span style="display:block;height:${PARA_GAP_EM}em"></span>`;

// Replace each run of 2+ consecutive <br>s (i.e. n-1 blank lines) with n-1
// spacer blocks of PARA_GAP_EM height. A display:block span forces its own
// line break, so `a<span…></span>b` renders a / gap / b. Single <br>s (plain
// line breaks, e.g. bullet lists) are untouched. Shared by the rich (Action
// Sheet templates) and plain-text (comments) display paths.
function brRunsToGaps(html: string): string {
  return html.replace(/(?:<br>){2,}/g, function (run) {
    return PARA_GAP_HTML.repeat(run.length / "<br>".length - 1);
  });
}

// richTextDisplayHtml — DISPLAY-ONLY transform of the sanitised HTML subset.
// NEVER store this output — the stored canonical form stays <br>-based;
// sanitizeRichText strips the spacer spans (no font-size), so they cannot
// leak into storage via a round-trip.
export function richTextDisplayHtml(html: string): string {
  return brRunsToGaps(sanitizeRichText(html));
}

// plainTextDisplayHtml — DISPLAY-ONLY transform for PLAIN-TEXT content
// (comments). Escapes FIRST (a typed <tag> stays visible text; nothing the
// user wrote is ever interpreted as HTML), then newlines become <br>s and
// blank lines the shared spacer blocks — so comments get the same paragraph
// spacing as the Action Sheets. Render via dangerouslySetInnerHTML; the
// newlines are consumed here, so a pre-wrap container won't double-space.
export function plainTextDisplayHtml(text: string): string {
  const brs = esc(String(text ?? ""))
    .replace(/\r\n/g, "\n")
    .replace(/\n/g, "<br>")
    .replace(/(<br>)+$/, "");
  return brRunsToGaps(brs);
}

// -----------------------------------------------------------------------------
// htmlToRuns — HTML -> lines of styled runs, for the PDF renderer. Empty -> [[]].
// -----------------------------------------------------------------------------
export function htmlToRuns(html: string): RichLine[] {
  const body = prepareBody(html);
  const lines: RichLine[] = [[]];

  function walk(node: Node, bold: boolean, underline: boolean, sizePt: number) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        // Preserve literal newlines (old plain-text templates) as line breaks.
        const segs = (child.textContent || "").split("\n");
        segs.forEach((seg, i) => {
          if (i > 0) lines.push([]);
          if (seg) lines[lines.length - 1].push({ text: seg, bold, underline, sizePt });
        });
        return;
      }
      if (child.nodeType !== 1) return;
      const el = child as Element;
      switch (el.tagName.toLowerCase()) {
        case "br":
          lines.push([]);
          break;
        case "b":
        case "strong":
          walk(el, true, underline, sizePt);
          break;
        case "u":
          walk(el, bold, true, sizePt);
          break;
        case "span": {
          const px = fontSizePx(el);
          walk(el, bold, underline, px && SIZE_PX_TO_PT[px] ? SIZE_PX_TO_PT[px] : sizePt);
          break;
        }
        default:
          walk(el, bold, underline, sizePt);
      }
    });
  }

  walk(body, false, false, BASE_PT);
  while (lines.length > 1 && lines[lines.length - 1].length === 0) lines.pop();
  return lines;
}
